import { useEffect, useState } from 'react';
import { propertiesApi } from '../../properties-api';
import {
  removeSpacesAndSymbols,
  validatePropertyCreationPayload,
} from '../../utils';
import { KNOCK_EMAIL_ERROR_MESSAGE, KNOCK_EMAIL_STATUS } from './constants';

export const usePropertyCreation = ({ createProperty }) => {
  const [newPropertyData, setNewPropertyData] = useState({});
  const [leasingTeams, setLeasingTeams] = useState([]);
  const [isNewAddressLineVisible, setIsNewAddressLineVisible] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [knockEmailStatus, setKnockEmailStatus] = useState(
    KNOCK_EMAIL_STATUS.IDLE,
  );

  const getPropertyTeamUsers = async () => {
    try {
      const teams = await propertiesApi.getLeasingTeams();
      setLeasingTeams(teams);
    } catch {
      return;
    }
  };

  useEffect(() => {
    getPropertyTeamUsers();
  }, []);

  const validateEmailUsername = async () => {
    clearErrorMessage(null, 'knockEmail');
    setKnockEmailStatus(KNOCK_EMAIL_STATUS.PROCESSING);
    try {
      const { knockEmail = '' } = newPropertyData;
      const isAvailable = await propertiesApi.getEmailAvailability(knockEmail);
      const status = isAvailable
        ? KNOCK_EMAIL_STATUS.SUCCESS
        : KNOCK_EMAIL_STATUS.ERROR;
      if (status === KNOCK_EMAIL_STATUS.ERROR) {
        setErrorMessages({
          knockEmail: KNOCK_EMAIL_ERROR_MESSAGE.USERNAME_TAKEN,
        });
      }
      setKnockEmailStatus(status);
    } catch {
      setKnockEmailStatus(KNOCK_EMAIL_STATUS.ERROR);
      setErrorMessages({
        knockEmail: KNOCK_EMAIL_ERROR_MESSAGE.SERVER_ISSUE,
      });
    }
  };

  const toggleAdditionalAddressLine = () => {
    setIsNewAddressLineVisible((prev) => !prev);
  };

  const validateData = () => {
    const errors = validatePropertyCreationPayload(
      newPropertyData,
      knockEmailStatus,
    );

    const hasErrors = Object.values(errors).some(Boolean);

    if (hasErrors) {
      setErrorMessages(errors);
    }

    return !hasErrors;
  };

  const updateProperty = (propertyUpdates) => {
    setNewPropertyData((prevState) => ({
      ...prevState,
      ...propertyUpdates,
    }));
  };

  const updateLogos = (updatedLogos) => {
    setNewPropertyData((prevState) => ({
      ...prevState,
      logos: updatedLogos,
    }));
  };

  const handleChange = (_, e) => {
    const { name, value } = e.target;
    updateProperty({ [name]: value });
  };

  const handleEmailChange = (email) => {
    const knockEmail = removeSpacesAndSymbols(email);
    updateProperty({ knockEmail: knockEmail });
    setKnockEmailStatus(KNOCK_EMAIL_STATUS.IDLE);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim().replace(/\s+/g, ' ');
    updateProperty({ [name]: trimmedValue });
  };

  const clearErrorMessage = (e, fieldName) => {
    const name = fieldName || e.target.name;
    if (errorMessages[name]) {
      setErrorMessages((prevState) => ({
        ...prevState,
        [name]: '',
      }));
    }
  };

  const handleSubmit = () => {
    const isValid = validateData();

    if (isValid) {
      createProperty(newPropertyData);
    }
  };

  const isSaveButtonDisabled =
    Object.values(errorMessages).some(Boolean) ||
    knockEmailStatus === KNOCK_EMAIL_STATUS.PROCESSING;

  return {
    clearErrorMessage,
    errorMessages,
    handleBlur,
    handleChange,
    handleEmailChange,
    handleSubmit,
    knockEmailStatus,
    leasingTeams,
    isNewAddressLineVisible,
    isSaveButtonDisabled,
    newPropertyData,
    toggleAdditionalAddressLine,
    updateLogos,
    validateEmailUsername,
  };
};
